/**
 * Address Form Section
 * Manages current, mailing, and previous addresses with history
 * Supports summary cards, expanded forms, delete confirmation, and draft states
 */

import * as React from "react";
import { differenceInYears, differenceInMonths } from "date-fns";
import { InformationDiamondIcon, Input, cn } from "@porchpass/ui";
import { Select, SelectItem } from "@porchpass/ui";
import { Button } from "@porchpass/ui";
import {
  FormTable,
  FormTableBody,
  FormTableBodyHeading,
  FormTableAddButton,
  useFormTableAddButtonState,
} from "@porchpass/ui";
import { FormFieldRow } from "@porchpass/ui";
import { Switch } from "@porchpass/ui";
import {
  RadioGroup,
  RadioGroupCardItem,
  useContainerRadiusSync,
} from "@porchpass/ui";
import { Autocomplete } from "@porchpass/ui";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogBody,
  DialogFooter,
  DialogTitle,
} from "@porchpass/ui";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "@porchpass/ui";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipArrow,
  TooltipProvider,
} from "@porchpass/ui";
import { useApplicationFormStore } from "../hooks/useApplicationFormStore";
import { AddressFields } from "./AddressFields";
import type { Address } from "../types/applicationTypes";
import { validateAddressField } from "../utils/validationSchemas";
import {
  Add01Icon,
  Alert01Icon,
  Cancel01Icon,
  Delete03Icon,
  Edit02Icon,
  Location03Icon,
  LocationStar01Icon,
  LocationUpdate01Icon,
  MoreVerticalIcon,
  UnavailableIcon,
  StarShape,
  MoonShape,
  CircleShape,
  Flower1Shape,
  Flower2Shape,
} from "@porchpass/ui";
import { DomainAddressType, DomainHousingType } from "@porchpass/api-client";
import { HOUSING_TYPE_OPTIONS } from "src/constants/enumOptions";
import {
  MONTH_OPTIONS,
  MONTH_LABELS,
  generateYearOptions,
} from "../constants/dateOptions";

// ============================================================================
// Constants
// ============================================================================

const YEAR_OPTIONS = generateYearOptions(50);

/**
 * Shape components cycled by card index for icon backgrounds.
 * Order matches the Figma designs — first 3 are fixed, then remaining shapes cycle.
 */
const ICON_SHAPES = [
  StarShape,
  MoonShape,
  CircleShape,
  Flower1Shape,
  Flower2Shape,
] as const;

// ============================================================================
// Helpers
// ============================================================================

interface AddressCardState {
  address: Address;
  isExpanded: boolean;
  key: string;
}

const createEmptyAddress = (
  type: Address["type"] = DomainAddressType.AddressCurrent,
): Address => ({
  id: crypto.randomUUID(),
  street: "",
  unit: "",
  city: "",
  state: "",
  zipCode: "",
  county: "",
  type,
  isMailingAddress: type === DomainAddressType.AddressCurrent,
  residedFromMonth: undefined,
  residedFromYear: undefined,
  residedToMonth: undefined,
  residedToYear: undefined,
  housingType: undefined,
  monthlyPayment: undefined,
});

/**
 * Copies address data from a source address to create a new address
 * with the specified type.
 */
const copyAddressData = (
  source: Address,
  type: DomainAddressType,
  isMailingAddress: boolean,
): Address => ({
  id: crypto.randomUUID(),
  street: source.street,
  unit: source.unit,
  city: source.city,
  state: source.state,
  zipCode: source.zipCode,
  county: source.county,
  type,
  isMailingAddress,
  residedFromMonth: source.residedFromMonth,
  residedFromYear: source.residedFromYear,
  residedToMonth: source.residedToMonth,
  residedToYear: source.residedToYear,
  housingType: source.housingType,
  housingTypeOther: source.housingTypeOther,
  monthlyPayment: source.monthlyPayment,
});

/**
 * Creates a new MAILING address with only the fields it needs (no dates, housing, payment).
 */
const createEmptyMailingAddress = (): Address => ({
  id: crypto.randomUUID(),
  street: "",
  unit: "",
  city: "",
  state: "",
  zipCode: "",
  county: "",
  type: DomainAddressType.AddressMailing,
  isMailingAddress: false,
  residedFromMonth: undefined,
  residedFromYear: undefined,
  residedToMonth: undefined,
  residedToYear: undefined,
  housingType: undefined,
  monthlyPayment: undefined,
});

// ============================================================================
// Copy From Primary Address Card Component
// ============================================================================

interface CopyFromPrimaryAddressCardProps {
  primaryCurrentAddress: Address;
  onCopyAddress: (primaryAddress: Address) => void;
}

const CopyFromPrimaryAddressCard: React.FC<CopyFromPrimaryAddressCardProps> = (
  props,
) => {
  const { primaryCurrentAddress, onCopyAddress } = props;

  const cityStateZip = [
    primaryCurrentAddress.city,
    primaryCurrentAddress.state,
    primaryCurrentAddress.zipCode,
  ]
    .filter(Boolean)
    .join(", ");

  return (
    <div className="px-4 py-3 mb-3 rounded-[16px] border bg-[#f0f4ff] border-[#3265ea]/20">
      <div className="flex gap-3">
        <InformationDiamondIcon size={20} className="shrink-0 text-primary" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-[#111827] mb-2">
            Same addresses as primary borrower?
          </p>
          <div className="mb-3 space-y-1">
            <p className="text-sm text-[#31394a] truncate">
              {primaryCurrentAddress.street}
              {primaryCurrentAddress.unit && (
                <span className="ml-1">{primaryCurrentAddress.unit}</span>
              )}
            </p>
            {cityStateZip && (
              <p className="text-xs text-[#4a5466]">{cityStateZip}</p>
            )}
          </div>
          <Button
            variant="primary"
            size="m"
            onClick={() => onCopyAddress(primaryCurrentAddress)}
            className="w-full"
          >
            Use this address
          </Button>
        </div>
      </div>
    </div>
  );
};

/**
 * An address is considered a "draft" when it's missing fields required for
 * final DLA submission. MAILING addresses only need address fields.
 */
const isAddressDraft = (addr: Address) => {
  if (addr.type === DomainAddressType.AddressMailing) {
    return !addr.street || !addr.city || !addr.state || !addr.zipCode;
  }
  return (
    !addr.street ||
    !addr.city ||
    !addr.state ||
    !addr.zipCode ||
    !addr.residedFromMonth ||
    !addr.residedFromYear ||
    (addr.type === DomainAddressType.AddressCurrent && !addr.housingType)
  );
};

const formatDateRange = (addr: Address): string | null => {
  const fromMonth = addr.residedFromMonth;
  const fromYear = addr.residedFromYear;
  if (!fromMonth || !fromYear) return null;

  const fromLabel = MONTH_LABELS[fromMonth] || fromMonth;
  const fromDate = new Date(parseInt(fromYear), parseInt(fromMonth) - 1);

  if (addr.type === DomainAddressType.AddressCurrent) {
    const now = new Date();
    const years = differenceInYears(now, fromDate);
    const months = differenceInMonths(now, fromDate) % 12;
    const durationParts: string[] = [];
    if (years > 0) durationParts.push(`${years} yr`);
    if (months > 0) durationParts.push(`${months} mo`);
    const duration = durationParts.join(" ") || "< 1 mo";
    return `${fromLabel} ${fromYear} - Present · ${duration}`;
  }

  // Previous address
  const toMonth = addr.residedToMonth;
  const toYear = addr.residedToYear;
  if (!toMonth || !toYear) return `${fromLabel} ${fromYear}`;

  const toLabel = MONTH_LABELS[toMonth] || toMonth;
  const toDate = new Date(parseInt(toYear), parseInt(toMonth) - 1);
  const years = differenceInYears(toDate, fromDate);
  const months = differenceInMonths(toDate, fromDate) % 12;
  const durationParts: string[] = [];
  if (years > 0) durationParts.push(`${years} yr`);
  if (months > 0) durationParts.push(`${months} mo`);
  const duration = durationParts.join(" ") || "< 1 mo";

  return `${fromLabel} ${fromYear} - ${toLabel} ${toYear} · ${duration}`;
};

// ============================================================================
// Props
// ============================================================================

interface AddressFormSectionProps {
  isCoBorrower?: boolean;
}

// ============================================================================
// Sub-components
// ============================================================================

// --- Summary Card ---
interface AddressSummaryCardProps {
  card: AddressCardState;
  index: number;
  hasErrors?: boolean;
  onEdit: () => void;
  onDelete: () => void;
}

const AddressSummaryCard: React.FC<AddressSummaryCardProps> = ({
  card,
  index,
  hasErrors = false,
  onEdit,
  onDelete,
}) => {
  const { address } = card;
  const isDraft = isAddressDraft(address);
  const isAddressUnknown = !address.street;
  const dateRange = formatDateRange(address);
  const isCurrent = address.type === DomainAddressType.AddressCurrent;
  const isMailing = address.type === DomainAddressType.AddressMailing;

  const cityStateZip = [address.city, address.state, address.zipCode]
    .filter(Boolean)
    .join(", ");

  // Pick shape component based on card index, cycling through available shapes
  const ShapeComponent = ICON_SHAPES[index % ICON_SHAPES.length];

  // Pick icon based on address type
  const IconComponent = isCurrent
    ? LocationStar01Icon
    : isMailing
      ? Location03Icon
      : LocationUpdate01Icon;

  return (
    <FormTableBody
      className={[
        "!p-4 transition-all duration-200 cursor-pointer hover:bg-[#f0f2ff] hover:!rounded-[16px]",
        isAddressUnknown ? "bg-amber-50" : undefined,
        hasErrors
          ? "shadow-[inset_0_0_0_2px_#dc2626] !rounded-[16px]"
          : isCurrent || isMailing
            ? "shadow-[inset_0_0_0_2px_#3265ea] !rounded-[16px]"
            : undefined,
      ]
        .filter(Boolean)
        .join(" ")}
      onClick={onEdit}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onEdit();
        }
      }}
    >
      <div className="flex gap-4 items-center">
        {/* Status icon with shape background */}
        <div className="relative w-8 h-8 shrink-0">
          {isAddressUnknown ? (
            <>
              <ShapeComponent
                size={32}
                fill="#fef3c7"
                className="absolute inset-0"
              />
              <div className="flex absolute inset-0 justify-center items-center">
                <Alert01Icon
                  variant="bulk-rounded"
                  size={16}
                  className="text-amber-500"
                />
              </div>
            </>
          ) : (
            <>
              <ShapeComponent
                size={32}
                fill={isCurrent || isMailing ? "#e5ebfd" : "#f0f1f4"}
                className="absolute inset-0"
              />
              <div className="flex absolute inset-0 justify-center items-center">
                <IconComponent
                  variant="bulk-rounded"
                  size={16}
                  className={
                    isCurrent || isMailing ? "text-primary" : "text-[#4a5466]"
                  }
                />
              </div>
            </>
          )}
        </div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          {/* Title row */}
          <div className="flex gap-1 items-center">
            <span className="text-[16px] font-medium text-[#111827] truncate">
              {isAddressUnknown ? "Address Unknown" : address.street}
            </span>
            {isCurrent && (
              <span className="inline-flex items-center h-[15px] px-1 rounded-[5px] bg-[#e5ebfd] text-[10px] font-semibold text-primary">
                Current
              </span>
            )}
            {(address.isMailingAddress || isMailing) && (
              <span className="inline-flex items-center h-[15px] px-1 rounded-[5px] bg-[#f3f5f8] text-[10px] font-semibold text-[#111827]">
                Mailing
              </span>
            )}
            {isDraft && (
              <span className="inline-flex items-center h-[15px] px-1 rounded-[5px] bg-amber-100 text-[10px] font-semibold text-amber-700">
                Draft
              </span>
            )}
          </div>

          {/* Subtitle */}
          {cityStateZip && (
            <p className="text-xs text-[#31394a] mt-0.5">{cityStateZip}</p>
          )}

          {/* Date range */}
          {dateRange && (
            <p className="text-xs text-[#4a5466] mt-0.5">{dateRange}</p>
          )}
        </div>

        {/* Right: monthly payment + kebab menu */}
        <div className="flex gap-2 items-center shrink-0">
          {address.monthlyPayment != null && (
            <div className="flex flex-col items-end">
              <span className="text-[16px] font-semibold text-[#111827]">
                ${address.monthlyPayment.toLocaleString()}
              </span>
              <span className="text-[10px] text-[#31394a]">
                {address.monthlyPayment === 0 ? "mo / ann" : "mo"}
              </span>
            </div>
          )}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon-sm">
                <MoreVerticalIcon size={16} />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={onEdit}>
                <Edit02Icon size={16} />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={onDelete}
                className="text-red-600 hover:text-white hover:bg-red-600 focus:text-white focus:bg-red-600"
              >
                <Delete03Icon size={16} />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </FormTableBody>
  );
};

// AddressFields extracted to ./AddressFields.tsx for reuse across sections

// --- Expanded Form ---
interface AddressExpandedFormProps {
  card: AddressCardState;
  index: number;
  isCoBorrower?: boolean;
  onUpdateAddress: (updates: Partial<Address>) => void;
  onDelete: () => void;
  googlePlacesApiKey?: string;
  needsMoreHistory?: boolean;
  hasOtherCurrent?: boolean;
  hasOtherMailing?: boolean;
  hasMailingAddressType?: boolean;
  onAddMailingAddress?: () => void;
}

const AddressExpandedForm: React.FC<AddressExpandedFormProps> = ({
  card,
  index,
  isCoBorrower = false,
  onUpdateAddress,
  onDelete,
  googlePlacesApiKey,
  needsMoreHistory = false,
  hasOtherCurrent = false,
  hasOtherMailing = false,
  hasMailingAddressType = false,
  onAddMailingAddress,
}) => {
  const store = useApplicationFormStore();
  const { address } = card;
  const isPrevious = address.type === DomainAddressType.AddressPrevious;
  const isMailing = address.type === DomainAddressType.AddressMailing;
  const [historyTooltipDismissed, setHistoryTooltipDismissed] =
    React.useState(false);
  const showHistoryTooltip =
    needsMoreHistory && !!address.residedFromYear && !historyTooltipDismissed;

  // Compute error map from store fieldErrors
  const borrowerPrefix = isCoBorrower ? "coBorrower" : "primaryBorrower";
  const errorPrefix = `${borrowerPrefix}.addresses.${index}.`;
  const errors = React.useMemo(() => {
    const result: Record<string, string> = {};
    const currentErrorPrefix = `${borrowerPrefix}.addresses._current.`;
    for (const [k, v] of Object.entries(store.fieldErrors)) {
      if (k.startsWith(errorPrefix)) {
        result[k.slice(errorPrefix.length)] = v;
      } else if (
        address.type === DomainAddressType.AddressCurrent &&
        k.startsWith(currentErrorPrefix)
      ) {
        result[k.slice(currentErrorPrefix.length)] = v;
      }
    }
    return result;
  }, [store.fieldErrors, errorPrefix, borrowerPrefix, address.type]);

  const handleFieldChange = <K extends keyof Address>(
    field: K,
    value: Address[K],
  ) => {
    // clearIfValid: if there's an existing error and new value is valid, clear immediately
    if (typeof value === "string" || typeof value === "number") {
      const errorKey = `${errorPrefix}${String(field)}`;
      const stringValue = String(value);
      if (
        store.fieldErrors[errorKey] &&
        !validateAddressField(String(field), stringValue)
      ) {
        store.clearFieldError(errorKey);
      }
    }

    // When changing to PREVIOUS, clear mailing flag and housing/payment fields
    if (field === "type" && value === DomainAddressType.AddressPrevious) {
      onUpdateAddress({
        type: value,
        isMailingAddress: false,
        housingType: undefined,
        housingTypeOther: undefined,
        monthlyPayment: undefined,
      });
      return;
    }

    onUpdateAddress({ [field]: value });
  };

  const handleMailingToggle = (checked: boolean) => {
    onUpdateAddress({ isMailingAddress: checked });
  };

  return (
    <FormTableBody>
      {/* Header */}
      <div className="flex justify-between items-center mb-4">
        <div>
          <p className="text-base font-medium text-[#111827]">
            {isMailing ? "Mailing Address" : "Address Details"}
          </p>
        </div>
        <Button
          variant="ghost"
          size="icon-sm"
          onClick={onDelete}
          className="rounded-full group/delete hover:bg-red-600"
        >
          <Delete03Icon
            size={16}
            className="text-red-500 group-hover/delete:text-white"
          />
        </Button>
      </div>

      {/* Address Type — MAILING shows a label, others show radio buttons */}
      {!isMailing && (
        <FormFieldRow label="Type">
          <RadioGroup
            value={address.type}
            onValueChange={(value) =>
              handleFieldChange("type", value as Address["type"])
            }
            className="grid grid-cols-2 gap-[9px]"
          >
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div className={cn(hasOtherCurrent && "cursor-not-allowed")}>
                    <RadioGroupCardItem
                      className={cn(
                        "w-full",
                        hasOtherCurrent && "pointer-events-none",
                      )}
                      value={DomainAddressType.AddressCurrent}
                      label="Current address"
                      variant="inline"
                      disabled={hasOtherCurrent}
                    />
                  </div>
                </TooltipTrigger>
                {hasOtherCurrent && (
                  <TooltipContent side="top">
                    Another address is already set as the current address
                  </TooltipContent>
                )}
              </Tooltip>
            </TooltipProvider>

            <RadioGroupCardItem
              value={DomainAddressType.AddressPrevious}
              label="Previous address"
              variant="inline"
            />
          </RadioGroup>
        </FormFieldRow>
      )}

      {/* Main address fields */}
      <AddressFields
        address={address}
        onFieldChange={handleFieldChange}
        onBulkChange={onUpdateAddress}
        showGooglePlaces={!!googlePlacesApiKey}
        googlePlacesApiKey={googlePlacesApiKey}
        errors={errors}
      />

      {/* Is mailing address toggle — CURRENT type only */}
      {!isMailing && !isPrevious && (
        <>
          <div className="flex gap-6 items-center">
            <div className="shrink-0 w-[128px]" />
            <TooltipProvider delayDuration={300}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <div
                    className={`flex-1 flex gap-4 items-center px-4 py-3 rounded-[52px] ${
                      address.isMailingAddress
                        ? "bg-[#e5ebfd]"
                        : hasOtherMailing || hasMailingAddressType
                          ? "bg-[#f0f1f4] opacity-60 cursor-not-allowed"
                          : "bg-[#f0f1f4]"
                    }`}
                  >
                    <Switch
                      checked={address.isMailingAddress}
                      onCheckedChange={handleMailingToggle}
                      disabled={
                        (hasOtherMailing || hasMailingAddressType) &&
                        !address.isMailingAddress
                      }
                    />
                    <span className="text-xs font-medium text-[#111827]">
                      Is mailing address
                    </span>
                  </div>
                </TooltipTrigger>
                {(hasOtherMailing || hasMailingAddressType) &&
                  !address.isMailingAddress && (
                    <TooltipContent side="top">
                      {hasMailingAddressType
                        ? "Mailing address is set separately. Delete the separate mailing address to be able to enable this option."
                        : "Another address is already set as the mailing address"}
                    </TooltipContent>
                  )}
              </Tooltip>
            </TooltipProvider>
          </div>

          {/* Inline "Add mailing address" button — CURRENT only */}
          {!address.isMailingAddress &&
            !hasMailingAddressType &&
            onAddMailingAddress && (
              <div className="flex gap-6 items-center">
                <div className="shrink-0 w-[128px]" />
                <Button
                  variant="secondary"
                  size="xs"
                  onClick={onAddMailingAddress}
                  className="self-start"
                >
                  <Add01Icon variant="stroke-rounded" size={12} />
                  Add mailing address
                </Button>
              </div>
            )}
        </>
      )}

      {/* Date fields — MAILING excluded */}
      {!isMailing && (
        <div className="pt-4 mt-6 space-y-2 border-t border-surface-container-disabled">
          {isPrevious ? (
            <>
              <FormTableBodyHeading title="Live here" />
              {/* From */}
              <FormFieldRow label="From">
                <div className="flex gap-2">
                  <div className="flex-1 min-w-0">
                    <Autocomplete
                      options={MONTH_OPTIONS}
                      value={address.residedFromMonth}
                      onValueChange={(v) =>
                        handleFieldChange("residedFromMonth", v)
                      }
                      rightSlot={
                        <span className="text-xs text-on-surface-muted">
                          Month
                        </span>
                      }
                      error={!!errors.residedFromMonth}
                      helperText={errors.residedFromMonth}
                    />
                  </div>
                  <TooltipProvider delayDuration={0}>
                    <Tooltip open={showHistoryTooltip}>
                      <TooltipTrigger asChild>
                        <div className="w-[148px] shrink-0">
                          <Autocomplete
                            options={YEAR_OPTIONS}
                            value={address.residedFromYear}
                            onValueChange={(v) =>
                              handleFieldChange("residedFromYear", v)
                            }
                            rightSlot={
                              <span className="text-xs text-on-surface-muted">
                                Year
                              </span>
                            }
                            error={!!errors.residedFromYear}
                            helperText={errors.residedFromYear}
                          />
                        </div>
                      </TooltipTrigger>
                      <TooltipContent
                        side="right"
                        className="max-w-[269px] !overflow-visible p-4"
                      >
                        <div className="flex justify-between items-center mb-3">
                          <div className="flex gap-2 items-center">
                            <div className="flex justify-center items-center w-6 h-6 bg-amber-100 rounded-full shrink-0">
                              <Alert01Icon
                                variant="bulk-rounded"
                                size={16}
                                className="text-amber-500"
                              />
                            </div>
                            <span className="text-base font-semibold text-white">
                              Just a heads up
                            </span>
                          </div>
                          <button
                            onClick={() => setHistoryTooltipDismissed(true)}
                            className="p-1 rounded-full hover:bg-white/10 shrink-0"
                          >
                            <Cancel01Icon size={14} className="text-white/60" />
                          </button>
                        </div>
                        <p className="text-xs font-medium text-white">
                          We are required to provide at least 2 years of
                          residential history.
                        </p>
                        <TooltipArrow />
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                </div>
              </FormFieldRow>

              {/* To */}
              <FormFieldRow label="To">
                <div className="flex gap-2">
                  <div className="flex-1 min-w-0">
                    <Autocomplete
                      options={MONTH_OPTIONS}
                      value={address.residedToMonth}
                      onValueChange={(v) =>
                        handleFieldChange("residedToMonth", v)
                      }
                      rightSlot={
                        <span className="text-xs text-on-surface-muted">
                          Month
                        </span>
                      }
                    />
                  </div>
                  <div className="w-[148px] shrink-0">
                    <Autocomplete
                      options={YEAR_OPTIONS}
                      value={address.residedToYear}
                      onValueChange={(v) =>
                        handleFieldChange("residedToYear", v)
                      }
                      rightSlot={
                        <span className="text-xs text-on-surface-muted">
                          Year
                        </span>
                      }
                    />
                  </div>
                </div>
              </FormFieldRow>
            </>
          ) : (
            <FormFieldRow label="Living here since">
              <div className="flex gap-2">
                <div className="flex-1 min-w-0">
                  <Autocomplete
                    options={MONTH_OPTIONS}
                    value={address.residedFromMonth}
                    onValueChange={(v) =>
                      handleFieldChange("residedFromMonth", v)
                    }
                    rightSlot={
                      <span className="text-xs text-on-surface-muted">
                        Month
                      </span>
                    }
                    error={!!errors.residedFromMonth}
                    helperText={errors.residedFromMonth}
                  />
                </div>
                <TooltipProvider delayDuration={0}>
                  <Tooltip open={showHistoryTooltip}>
                    <TooltipTrigger asChild>
                      <div className="w-[148px] shrink-0">
                        <Autocomplete
                          options={YEAR_OPTIONS}
                          value={address.residedFromYear}
                          onValueChange={(v) =>
                            handleFieldChange("residedFromYear", v)
                          }
                          rightSlot={
                            <span className="text-xs text-on-surface-muted">
                              Year
                            </span>
                          }
                          error={!!errors.residedFromYear}
                          helperText={errors.residedFromYear}
                        />
                      </div>
                    </TooltipTrigger>
                    <TooltipContent
                      side="right"
                      className="max-w-[269px] !overflow-visible p-4"
                    >
                      <div className="flex justify-between items-center mb-3">
                        <div className="flex gap-2 items-center">
                          <div className="flex justify-center items-center w-6 h-6 bg-amber-100 rounded-full shrink-0">
                            <Alert01Icon
                              variant="bulk-rounded"
                              size={16}
                              className="text-amber-500"
                            />
                          </div>
                          <span className="text-base font-semibold text-white">
                            Just a heads up
                          </span>
                        </div>
                        <button
                          onClick={() => setHistoryTooltipDismissed(true)}
                          className="p-1 rounded-full hover:bg-white/10 shrink-0"
                        >
                          <Cancel01Icon size={14} className="text-white/60" />
                        </button>
                      </div>
                      <p className="text-xs font-medium text-white">
                        We are required to provide at least 2 years of
                        residential history.
                      </p>
                      <TooltipArrow />
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </FormFieldRow>
          )}
        </div>
      )}

      {/* Housing type + Monthly payment (current address only, MAILING excluded) */}
      {!isPrevious && !isMailing && (
        <>
          <FormFieldRow label="Housing type">
            <Select
              value={address.housingType || ""}
              onValueChange={(value: string) => {
                const newType = value as Address["housingType"];
                const updates: Partial<Address> = { housingType: newType };
                if (
                  newType !== DomainHousingType.HousingOther &&
                  newType !== DomainHousingType.HousingOwn
                ) {
                  updates.housingTypeOther = undefined;
                }
                onUpdateAddress(updates);
              }}
              error={!!errors.housingType}
              helperText={errors.housingType}
            >
              {HOUSING_TYPE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </Select>
          </FormFieldRow>

          {(address.housingType === DomainHousingType.HousingOther ||
            address.housingType === DomainHousingType.HousingOwn) && (
            <FormFieldRow
              label={
                address.housingType === DomainHousingType.HousingOwn
                  ? "Plans for home"
                  : "Specify other"
              }
            >
              <Input
                value={address.housingTypeOther || ""}
                onChange={(e) =>
                  handleFieldChange("housingTypeOther", e.target.value)
                }
                placeholder={
                  address.housingType === DomainHousingType.HousingOwn
                    ? "e.g., Selling current home"
                    : "e.g., Living with family"
                }
                required
                error={
                  !!errors.housingTypeOther &&
                  (address.housingType === DomainHousingType.HousingOwn ||
                    address.housingType === DomainHousingType.HousingOther)
                }
                helperText={errors.housingTypeOther}
              />
            </FormFieldRow>
          )}

          {address.housingType !==
            DomainHousingType.HousingNoPrimaryExpense && (
            <FormFieldRow label="Monthly payment">
              <Input
                inputMode="decimal"
                value={
                  address.monthlyPayment != null
                    ? String(address.monthlyPayment)
                    : ""
                }
                onChange={(e) => {
                  const val = e.target.value;
                  handleFieldChange(
                    "monthlyPayment",
                    val === "" ? undefined : Number(val),
                  );
                }}
                leftSlot={<span className="text-on-surface-muted">$</span>}
                rightSlot={
                  <span className="text-xs text-on-surface-muted">
                    Primary borrower amount only
                  </span>
                }
                error={!!errors.monthlyPayment}
                helperText={errors.monthlyPayment}
              />
            </FormFieldRow>
          )}
        </>
      )}
    </FormTableBody>
  );
};

// --- Delete Dialog ---
interface DeleteAddressDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onConfirm: () => void;
}

const DeleteAddressDialog: React.FC<DeleteAddressDialogProps> = ({
  open,
  onOpenChange,
  onConfirm,
}) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-sm">
      <DialogHeader>
        <div className="flex gap-2 items-center">
          <UnavailableIcon size={18} className="text-red-600" />
          <DialogTitle>Delete Address</DialogTitle>
        </div>
      </DialogHeader>
      <DialogBody>
        <p className="text-base text-[#4a5466]">
          Are you sure you want to delete this address?
        </p>
      </DialogBody>
      <DialogFooter>
        <Button
          variant="secondary"
          size="s"
          onClick={() => onOpenChange(false)}
        >
          Cancel
        </Button>
        <Button variant="destructive-secondary" size="s" onClick={onConfirm}>
          Yes, delete address
        </Button>
      </DialogFooter>
    </DialogContent>
  </Dialog>
);

// ============================================================================
// Main Component
// ============================================================================

export const AddressFormSection: React.FC<AddressFormSectionProps> = ({
  isCoBorrower = false,
}) => {
  const keyCounterRef = React.useRef(0);
  const generateKey = () => `addr-${++keyCounterRef.current}`;

  const store = useApplicationFormStore();
  const borrower = isCoBorrower
    ? store.data.coBorrower
    : store.data.primaryBorrower;
  const borrowerType = isCoBorrower ? "co" : "primary";
  const addButtonState = useFormTableAddButtonState();
  const containerStyles = useContainerRadiusSync({
    adjacentToBottom: true,
    bottomExpanded: addButtonState.isExpanded,
  });

  // Card states
  const [addressCards, setAddressCards] = React.useState<AddressCardState[]>(
    [],
  );

  // Delete confirmation
  const [deleteTargetIndex, setDeleteTargetIndex] = React.useState<
    number | null
  >(null);

  // Backend tracks whether the borrower has sufficient residential history
  const needsMoreHistory = !(borrower?.hasSufficientAddressHistory ?? false);

  // Sync with store on mount
  const addresses = borrower?.addresses || [];
  React.useEffect(() => {
    if (addresses.length > 0 && addressCards.length === 0) {
      setAddressCards(
        addresses.map((addr) => ({
          address: addr,
          isExpanded: false,
          key: generateKey(),
        })),
      );
    }
  }, []);

  if (!borrower) return null;

  const borrowerPrefix = isCoBorrower ? "coBorrower" : "primaryBorrower";
  const currentAddressError =
    store.fieldErrors[`${borrowerPrefix}.addresses._current`];

  const googlePlacesApiKey = __GOOGLE_PLACES_API_KEY__;

  // Co-borrower copy from primary logic
  const primaryBorrower = store.data.primaryBorrower;
  const primaryCurrentAddress = primaryBorrower?.addresses.find(
    (addr) => addr.type === DomainAddressType.AddressCurrent,
  );
  const coBorrowerHasCurrentAddress = addressCards.some(
    (c) => c.address.type === DomainAddressType.AddressCurrent,
  );

  // MAILING address type checks
  const hasMailingAddressType = addressCards.some(
    (c) => c.address.type === DomainAddressType.AddressMailing,
  );
  const hasOtherMailingFlag = addressCards.some(
    (c) =>
      c.address.isMailingAddress &&
      c.address.type !== DomainAddressType.AddressMailing,
  );

  // Check if there's a mailing address: either a CURRENT with isMailingAddress=true or a MAILING type
  const hasMailingAddress = hasOtherMailingFlag || hasMailingAddressType;
  const noMailingAddressError = !hasMailingAddress;

  const handleCopyAddress = (primaryAddress: Address) => {
    const copied = copyAddressData(
      primaryAddress,
      DomainAddressType.AddressCurrent,
      true,
    );
    store.addAddress("co", copied);

    // Auto-collapse all existing cards, expand new one
    setAddressCards((prev) => [
      ...prev.map((c) => ({ ...c, isExpanded: false })),
      {
        address: copied,
        isExpanded: true,
        key: generateKey(),
      },
    ]);
  };

  // --- Actions ---

  const handleAddAddress = () => {
    if (store.fieldErrors[`${borrowerPrefix}.addresses._current`]) {
      store.clearFieldError(`${borrowerPrefix}.addresses._current`);
    }
    const hasCurrent = addressCards.some(
      (c) => c.address.type === DomainAddressType.AddressCurrent,
    );
    const isFirst = addressCards.length === 0;
    const newAddress = createEmptyAddress(
      hasCurrent
        ? DomainAddressType.AddressPrevious
        : DomainAddressType.AddressCurrent,
    );
    if (isFirst) newAddress.isMailingAddress = true;

    store.addAddress(borrowerType, newAddress);

    // Auto-collapse all existing cards, expand new one
    setAddressCards((prev) => [
      ...prev.map((c) => ({ ...c, isExpanded: false })),
      {
        address: newAddress,
        isExpanded: true,
        key: generateKey(),
      },
    ]);
  };

  const handleAddMailingAddress = () => {
    const mailingAddress = createEmptyMailingAddress();
    store.addAddress(borrowerType, mailingAddress);

    // Auto-collapse all existing cards, expand new one
    setAddressCards((prev) => [
      ...prev.map((c) => ({ ...c, isExpanded: false })),
      {
        address: mailingAddress,
        isExpanded: true,
        key: generateKey(),
      },
    ]);
  };

  const handleEdit = (index: number) => {
    setAddressCards((prev) =>
      prev.map((c, i) => ({
        ...c,
        isExpanded: i === index,
      })),
    );
  };

  const handleDeleteRequest = (index: number) => {
    setDeleteTargetIndex(index);
  };

  const handleDeleteConfirm = () => {
    if (deleteTargetIndex === null) return;

    store.removeAddress(borrowerType, deleteTargetIndex);
    setAddressCards((prev) => prev.filter((_, i) => i !== deleteTargetIndex));
    setDeleteTargetIndex(null);
  };

  const handleUpdateAddress = (index: number, updates: Partial<Address>) => {
    setAddressCards((prev) => {
      const newCards = [...prev];
      newCards[index] = {
        ...newCards[index],
        address: { ...newCards[index].address, ...updates },
      };
      return newCards;
    });
    store.updateAddress(borrowerType, index, updates);
  };

  return (
    <FormTable
      title={
        <span className="flex gap-1 items-center">
          Address
          {addressCards.length > 1 && (
            <span className="inline-flex items-center justify-center h-5 min-w-5 px-1 rounded-[5px] bg-[#e5ebfd] text-[10px] font-semibold text-primary">
              {addressCards.length}
            </span>
          )}
        </span>
      }
      headerIcon={
        <Location03Icon
          variant="bulk-rounded"
          size={16}
          className="text-[#226392]"
        />
      }
      style={containerStyles.style}
    >
      {/* Copy from Primary Borrower card (co-borrower only) */}
      {isCoBorrower &&
        primaryCurrentAddress &&
        !coBorrowerHasCurrentAddress && (
          <CopyFromPrimaryAddressCard
            primaryCurrentAddress={primaryCurrentAddress}
            onCopyAddress={handleCopyAddress}
          />
        )}

      {currentAddressError && (
        <div className="px-4 py-2">
          <p className="text-sm text-[#872525]">{currentAddressError}</p>
        </div>
      )}

      {/* Mailing address validation error */}
      {noMailingAddressError && (
        <div className="px-4 py-2">
          <p className="text-sm text-[#872525]">
            No mailing address present. Either set your current address as your
            mailing address, or{" "}
            <button
              type="button"
              onClick={handleAddMailingAddress}
              className="underline cursor-pointer hover:no-underline"
            >
              add a separate mailing address
            </button>
            .
          </p>
        </div>
      )}

      {/* Render address cards */}
      {addressCards.map((card, index) => {
        const errorPrefix = `${borrowerPrefix}.addresses.${index}.`;
        const hasErrors = Object.keys(store.fieldErrors).some((k) =>
          k.startsWith(errorPrefix),
        );
        const hasOtherCurrent = addressCards.some(
          (c, i) =>
            i !== index && c.address.type === DomainAddressType.AddressCurrent,
        );
        const hasOtherMailing = addressCards.some(
          (c, i) => i !== index && c.address.isMailingAddress,
        );

        const isCurrent =
          card.address.type === DomainAddressType.AddressCurrent;

        // hasOtherMailing for the toggle: includes both flag-based and type-based
        const effectiveHasOtherMailing =
          hasOtherMailing || hasMailingAddressType;

        return card.isExpanded ? (
          <AddressExpandedForm
            key={card.key}
            card={card}
            index={index}
            isCoBorrower={isCoBorrower}
            onUpdateAddress={(updates) => handleUpdateAddress(index, updates)}
            onDelete={() => handleDeleteRequest(index)}
            googlePlacesApiKey={googlePlacesApiKey}
            needsMoreHistory={needsMoreHistory}
            hasOtherCurrent={hasOtherCurrent}
            hasOtherMailing={effectiveHasOtherMailing}
            hasMailingAddressType={hasMailingAddressType}
            onAddMailingAddress={
              isCurrent && !hasMailingAddressType
                ? handleAddMailingAddress
                : undefined
            }
          />
        ) : (
          <AddressSummaryCard
            key={card.key}
            card={card}
            index={index}
            hasErrors={hasErrors}
            onEdit={() => handleEdit(index)}
            onDelete={() => handleDeleteRequest(index)}
          />
        );
      })}

      {/* Add address button */}
      <FormTableAddButton
        onClick={handleAddAddress}
        {...addButtonState.interactionProps}
      >
        <Add01Icon variant="stroke-rounded" size={12} />
        {isCoBorrower && primaryCurrentAddress && !coBorrowerHasCurrentAddress
          ? "Use a different address"
          : addressCards.length === 0
            ? "Add address"
            : "Additional address"}
      </FormTableAddButton>

      {/* Delete confirmation dialog */}
      <DeleteAddressDialog
        open={deleteTargetIndex !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTargetIndex(null);
        }}
        onConfirm={handleDeleteConfirm}
      />
    </FormTable>
  );
};

export default AddressFormSection;
